// Convolve verb — apply an impulse response (any sample) to a source.
// "Drum × room recording = drums in that room" is the classic use, but
// any two samples can be convolved for textural results.

import { useCallback, useState } from 'react';
import { applyConvolve } from './convolve';
import { fromPcm, toPcm } from '../../pipeline';
import { SourcePicker, type PickedSource } from '../SourcePicker';
import type { VerbProps } from '../types';

export function ConvolveVerb({
  audioContext,
  onBuffer,
  onStatus,
}: VerbProps) {
  const [source, setSource] = useState<PickedSource | null>(null);
  const [ir, setIr] = useState<PickedSource | null>(null);
  const [normalize, setNormalize] = useState(true);
  const [rendering, setRendering] = useState(false);

  const handleRender = useCallback(async () => {
    if (!source || !ir || !audioContext) return;
    setRendering(true);
    try {
      const result = await applyConvolve(
        toPcm(source.buffer),
        toPcm(ir.buffer),
        { normalize },
      );
      const buffer = fromPcm(audioContext, result);
      const sourceIds = [source.sampleId, ir.sampleId].filter(
        (id): id is string => typeof id === 'string',
      );
      onBuffer(buffer, {
        suggestedName: `${source.name}_x_${ir.name}`,
        origin: {
          kind: 'convolve',
          sourceSampleId: source.sampleId,
          irSampleId: ir.sampleId,
          normalize,
        },
        sourceSampleIds: sourceIds.length > 0 ? sourceIds : undefined,
      });
      onStatus('Rendered — trim and save.', 'ok');
    } catch (e) {
      onStatus(
        'Render failed: ' + (e instanceof Error ? e.message : String(e)),
        'err',
      );
    } finally {
      setRendering(false);
    }
  }, [source, ir, audioContext, normalize, onBuffer, onStatus]);

  return (
    <div className="flex flex-col gap-3 min-h-[200px]">
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-400">
        Source × IR. Try a vocal as the source and a metallic clang as the IR
        for resonant, bell-like textures.
      </div>
      <SourcePicker
        audioContext={audioContext}
        selectedId={source?.sampleId}
        onPicked={setSource}
        onStatus={onStatus}
        label="Source"
      />
      <SourcePicker
        audioContext={audioContext}
        selectedId={ir?.sampleId}
        onPicked={setIr}
        onStatus={onStatus}
        label="Impulse response"
      />
      <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 cursor-pointer">
        <input
          type="checkbox"
          checked={normalize}
          onChange={(e) => setNormalize(e.target.checked)}
          className="accent-yellow-300"
        />
        <span>Normalize IR (recommended)</span>
      </label>
      <button
        type="button"
        onClick={() => void handleRender()}
        disabled={!source || !ir || rendering}
        className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
      >
        {rendering ? 'Rendering…' : '● Render'}
      </button>
    </div>
  );
}
